import React from 'react';
import { AlertOctagon, CheckCircle2, ShieldAlert } from 'lucide-react';
import { useEmergency } from '../../context/EmergencyContext';
import { formatRelativeTime } from '@sahaj/utils';

export const IncomingEmergencyModal: React.FC = () => {
  const { activeAlerts, acknowledgeAlert } = useEmergency();

  if (activeAlerts.length === 0) return null;

  // Render the most recent unacknowledged emergency alert
  const currentAlert = activeAlerts[0];
  if (!currentAlert) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in zoom-in-95 duration-200">
      <div className="w-full max-w-md bg-card border-4 border-destructive rounded-3xl shadow-2xl overflow-hidden flex flex-col relative">
        {/* Animated Pulse Header Banner */}
        <div className="bg-destructive text-destructive-foreground px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-2xl animate-bounce">
              <AlertOctagon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold uppercase tracking-wide">🚨 EMERGENCY ALERT</h2>
              <p className="text-xs text-white/80 font-medium">Urgent Broadcast Received</p>
            </div>
          </div>
          {activeAlerts.length > 1 && (
            <span className="bg-white/20 px-2.5 py-1 rounded-full text-xs font-bold text-white">
              1 of {activeAlerts.length}
            </span>
          )}
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5">
          {/* Sender Details */}
          <div className="flex items-center gap-3.5 p-3 bg-muted/40 border border-border/60 rounded-2xl">
            {currentAlert.senderAvatarUrl ? (
              <img src={currentAlert.senderAvatarUrl} alt="" className="h-10 w-10 rounded-full object-cover border-2 border-destructive/50" />
            ) : (
              <div className="h-10 w-10 rounded-full bg-destructive/20 text-destructive font-bold text-sm flex items-center justify-center border-2 border-destructive/50">
                {currentAlert.senderName.charAt(0)}
              </div>
            )}
            <div>
              <div className="text-xs text-muted-foreground font-medium">Alert Issued By</div>
              <div className="text-sm font-bold text-foreground flex items-center gap-1.5">
                <span>{currentAlert.senderName}</span>
                {currentAlert.targetScope === 'ADMINS' && (
                  <span className="text-[10px] bg-destructive/15 text-destructive px-2 py-0.5 rounded-md font-semibold flex items-center gap-1">
                    <ShieldAlert className="h-3 w-3" /> Admin Only
                  </span>
                )}
              </div>
            </div>
            <div className="ml-auto text-right text-[11px] text-muted-foreground font-medium">
              {formatRelativeTime(currentAlert.createdAt)}
            </div>
          </div>

          {/* Emergency Message Content (Text safe) */}
          <div className="p-4 bg-destructive/10 border-2 border-destructive/30 rounded-2xl">
            <p className="text-base font-semibold text-foreground whitespace-pre-wrap leading-relaxed">
              {currentAlert.message}
            </p>
          </div>

          {/* Action Button */}
          <div className="pt-2">
            <button
              onClick={() => acknowledgeAlert(currentAlert.id)}
              className="w-full py-3.5 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-extrabold text-sm uppercase tracking-wider rounded-2xl shadow-xl shadow-destructive/30 transition transform active:scale-95 flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="h-5 w-5" />
              <span>ACKNOWLEDGE EMERGENCY</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
